// angular
import {inject, Injectable, signal} from "@angular/core";
import {HttpClient} from "@angular/common/http";
// services
import {StorageService} from "@shared/services/storage.service";
import {UserService} from "@shared/services/user.service";
// interfaces
import {
  IResendOtp,
  ISignedIn,
  ISignup,
  ISignupSignal,
} from "@auth/shared/interfaces/signup.interface";
import {ILoggedIn, ILogin} from "@auth/shared/interfaces/login.interface";
import {
  IForgotPass,
  IForgottenPass,
  IReqForgotPass,
  IResetPassSignal,
} from "../interfaces/forgot.interface";
// env & utils
import {environment} from "@environments/environment";
import parseJwt from "@shared/utils/jwt-parser";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  userSignUpState = signal<Partial<ISignupSignal>>({state: 0});
  userForgotPassState = signal<Partial<IResetPassSignal>>({state: 0});
  cashUrlForAfterLogin = signal<string | undefined>(undefined);

  private readonly _http = inject(HttpClient);
  private readonly _storageService = inject(StorageService);
  private readonly _userService = inject(UserService);

  public updateUserSignUpState(updatedField: Partial<ISignupSignal>) {
    this.userSignUpState.set({...this.userSignUpState(), ...updatedField});
  }

  public updateUserForgotPassState(updatedField: Partial<IResetPassSignal>) {
    this.userForgotPassState.set({
      ...this.userForgotPassState(),
      ...updatedField,
    });
  }

  isLoggedIn() {
    try {
      const token = this._storageService.getToken();
      if (!token) return false;
      const parsedPayload = parseJwt(token);
      const currentTime = new Date().getTime() / 1000;
      return parsedPayload.exp > currentTime;
    } catch (error) {
      console.error("Failed to parse token:", error);
      return false;
    }
  }

  signUp(params: ISignup) {
    return this._http.post<ISignedIn>("auth/register", {...params});
  }

  resendOtp(params: IResendOtp) {
    return this._http.post<{hash: string}>("auth/resend-otp", params, {
      headers: {
        client: "meat",
      },
    });
  }

  login(params: ILogin) {
    return this._http.post<ILoggedIn>("auth/login", params, {
      headers: {
        client: environment.client,
      },
    });
  }

  logout() {
    return this._http.post(
      "auth/logout",
      {},
      {
        headers: {
          client: environment.client,
        },
      }
    );
  }

  refreshToken() {
    return this._http
      .post<{
        access_token: string;
      }>("auth/refresh", {
        refresh_token: this._storageService.getRefreshToken(),
      })
      .subscribe((res) => {
        this._storageService.setToken(res.access_token);
        this._userService.createUserFromToken(res.access_token);
      });
  }

  requestForgotPassword(params: IReqForgotPass) {
    return this._http.post<{hash: string}>(
      "auth/request-forgot-password",
      params,
      {
        headers: {
          client: "meat",
        },
      }
    );
  }

  forgotPassword(params: IForgotPass) {
    return this._http.post<IForgottenPass>("auth/forgot-password", params, {
      headers: {
        client: "meat",
      },
    });
  }
}
